import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';

import type { GlobalDialogProps } from '../../../types';
import { RootState } from '../../../reducers';
import { screenAppAPIClient } from '../../../lib/baseAxios';
import { getJoinUrlThunk } from '../teamSlice';

export const InviteLinkDialog = ({ onClose }: GlobalDialogProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { team } = useSelector((state: RootState) => state);
  const { teamId, joinCode } = team?.selectedTeam || {};
  const [emailInputs, setEmailInputs] = useState<string[]>(['']);
  const [emailList, setEmailList] = useState<string[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [showPrivacyInfo, setShowPrivacyInfo] = useState(false);

  useEffect(() => {
    if (!joinCode && teamId) {
      dispatch(getJoinUrlThunk(teamId));
    }
  }, [dispatch, joinCode, teamId]);

  const validateEmail = (input: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(input.toLowerCase());
  };

  const handleEmailChange = (index: number, value: string) => {
    const newInputs = [...emailInputs];
    newInputs[index] = value;
    setEmailInputs(newInputs);

    // If typing in the last input and it has a character, add a new input
    if (index === emailInputs.length - 1 && value.length > 0) {
      setEmailInputs([...newInputs, '']);
    }

    // Update the email list with valid emails
    const validEmails = newInputs
      .map((email) => email.trim())
      .filter((email) => email.length > 0 && validateEmail(email));
    setEmailList(validEmails);
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      const currentValue = emailInputs[index].trim();

      if (currentValue && validateEmail(currentValue)) {
        // Move focus to next input or create new one
        const nextInput = document.querySelector(
          `input[name="email-${index + 1}"]`,
        ) as HTMLInputElement;
        if (nextInput) {
          nextInput.focus();
        }
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent, index: number) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');

    const emails = pastedText
      .split(/[,;\n\s]+/)
      .map((email) => email.trim())
      .filter((email) => email.length > 0);

    if (emails.length > 0) {
      const newInputs = [...emailInputs];
      newInputs.splice(index, 1, ...emails);

      // Add empty input at the end if last one has content
      if (newInputs[newInputs.length - 1].trim().length > 0) {
        newInputs.push('');
      }

      setEmailInputs(newInputs);

      // Update valid emails list
      const validEmails = newInputs
        .map((email) => email.trim())
        .filter((email) => email.length > 0 && validateEmail(email));
      setEmailList(validEmails);
    }
  };

  const handleSendInvites = async () => {
    try {
      if (!teamId) {
        toast.error(t('invite.email.unableToGenerate'));
        return;
      }

      if (emailList.length === 0) {
        toast.error(t('invite.email.errors.required'));
        return;
      }

      setIsSending(true);

      await screenAppAPIClient.files.sendInvitation(teamId, {
        emails: emailList,
        role: 'Member',
      });

      toast(t('invite.email.invitationsSent', { count: emailList.length }));
      setEmailInputs(['']);
      setEmailList([]);
    } catch (error) {
      console.error('Failed to send invitations:', error);
      toast.error(t('invite.email.sendFailed'));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open onOpenChange={() => onClose?.()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('invite.title', 'Invite people')}</DialogTitle>
          <DialogDescription>
            {t(
              'invite.description',
              'Share your videos and collaborate with AI',
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => setShowPrivacyInfo(!showPrivacyInfo)}
              className="flex w-full items-center justify-between text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              <span>
                {t(
                  'invite.privacy.question',
                  'Will my team members see my recordings?',
                )}
              </span>
              <ChevronDown
                className={cn(
                  'h-4 w-4 transition-transform duration-200',
                  showPrivacyInfo && 'rotate-180',
                )}
              />
            </button>
            <div
              className={cn(
                'overflow-hidden transition-all duration-200',
                showPrivacyInfo ? 'max-h-24 opacity-100' : 'max-h-0 opacity-0',
              )}
            >
              <p className="text-sm text-muted-foreground">
                {t(
                  'invite.privacy.answer',
                  "Your recordings won't be automatically shared. They will only see your recordings if you share them.",
                )}
              </p>
            </div>
          </div>

          <div className="space-y-3 max-h-48 overflow-y-auto">
            {emailInputs.map((email, index) => {
              const isValid =
                email.trim().length > 0 && validateEmail(email.trim());
              const isInvalid =
                email.trim().length > 0 && !validateEmail(email.trim());

              return (
                <input
                  key={index}
                  name={`email-${index}`}
                  type="email"
                  placeholder={t(
                    'invite.email.placeholder',
                    'Enter email addresses',
                  )}
                  value={email}
                  onChange={(e) => handleEmailChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onPaste={(e) => handlePaste(e, index)}
                  className={cn(
                    'flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
                    isInvalid && 'border-destructive',
                    isValid && 'border-green-500',
                    !isValid && !isInvalid && 'border-input',
                  )}
                />
              );
            })}
          </div>

          <button
            type="button"
            onClick={handleSendInvites}
            disabled={isSending || emailList.length === 0}
            className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-6"
          >
            {isSending ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                {t('invite.email.sending', 'Sending...')}
              </>
            ) : (
              t('invite.email.send', 'Add members free')
            )}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InviteLinkDialog;
